class EconoSmartCatalog {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.ajaxEnabled = root.dataset.ajaxEnabled === 'true';
    this.gridStorageKey = 'econosmart:catalog-columns';
    this.abortController = null;
    this.lastFilterTrigger = null;

    this.bind();
    this.applyGridPreference();
  }

  bind() {
    this.root.addEventListener('click', (event) => this.handleClick(event));
    this.root.addEventListener('change', (event) => this.handleChange(event));
    this.root.addEventListener('submit', (event) => this.handleSubmit(event));

    document.addEventListener('keydown', this.handleEscape);
  }

  handleEscape = (event) => {
    if (event.key === 'Escape') this.closeDrawer();
  };

  handleClick(event) {
    const open = event.target.closest('[data-filter-open]');
    const close = event.target.closest('[data-filter-close]');
    const facetLink = event.target.closest('[data-facet-link]');
    const paginationLink = event.target.closest('.pagination a');
    const gridButton = event.target.closest('[data-grid-columns]');

    if (open) {
      event.preventDefault();
      this.openDrawer(open);
      return;
    }

    if (close) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    if (gridButton) {
      event.preventDefault();
      this.setGridColumns(Number(gridButton.dataset.gridColumns));
      return;
    }

    const link = facetLink || paginationLink;
    if (link && this.ajaxEnabled) {
      event.preventDefault();
      this.load(link.href, { pushState: true, scrollToResults: Boolean(paginationLink) });
    }
  }

  handleChange(event) {
    const sort = event.target.closest('[data-sort-select]');
    if (sort) {
      if (!this.ajaxEnabled) {
        sort.form?.submit();
        return;
      }

      const url = this.formUrl(sort.form);
      this.load(url, { pushState: true });
      return;
    }

    const form = event.target.closest('[data-facets-form]');
    if (!form || !this.ajaxEnabled) return;

    if (event.target.matches('input[type="checkbox"]')) {
      const url = this.formUrl(form);
      const preserveDrawer = !form.hasAttribute('data-auto-filter');
      this.load(url, { pushState: true, preserveDrawer });
    }
  }

  handleSubmit(event) {
    const form = event.target.closest('[data-facets-form], [data-sort-form]');
    if (!form || !this.ajaxEnabled) return;

    event.preventDefault();
    this.closeDrawer();
    const url = this.formUrl(form);
    this.load(url, { pushState: true, scrollToResults: true });
  }

  formUrl(form) {
    const url = new URL(window.location.href);
    url.search = '';

    const data = new FormData(form);
    for (const [key, value] of data.entries()) {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    }

    return url.toString();
  }

  openDrawer(trigger) {
    const drawer = this.root.querySelector('[data-filter-drawer]');
    if (!drawer) return;

    this.lastFilterTrigger = trigger;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('filter-drawer-open');
    window.setTimeout(() => drawer.querySelector('[data-filter-close]')?.focus(), 50);
  }

  closeDrawer() {
    const drawer = this.root?.querySelector('[data-filter-drawer]');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('filter-drawer-open');
    this.lastFilterTrigger?.focus();
  }

  async load(url, { pushState = true, scrollToResults = false, preserveDrawer = false } = {}) {
    if (!this.ajaxEnabled || !this.sectionId) {
      window.location.href = url;
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const previousDrawer = this.root.querySelector('[data-filter-drawer]');
    const drawerScrollTop = preserveDrawer ? previousDrawer?.querySelector('.filter-drawer__panel')?.scrollTop || 0 : 0;
    const activeFilter = preserveDrawer ? document.activeElement?.closest('label')?.querySelector('input') : null;
    const activeFilterName = activeFilter?.name || '';
    const activeFilterValue = activeFilter?.value || '';

    this.setLoading(true);

    const cleanUrl = new URL(url, window.location.origin);
    cleanUrl.searchParams.delete('section_id');

    const requestUrl = new URL(cleanUrl.toString());
    requestUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(requestUrl.toString(), {
        headers: { Accept: 'text/html' },
        signal: this.abortController.signal
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-facets-root]');
      if (!incoming) throw new Error('No se pudo renderizar el catálogo.');

      this.root.replaceWith(incoming);
      this.root = incoming;
      this.sectionId = incoming.dataset.sectionId || this.sectionId;
      this.ajaxEnabled = incoming.dataset.ajaxEnabled === 'true';
      this.bindAfterRender();
      this.applyGridPreference();

      if (preserveDrawer) {
        const drawer = this.root.querySelector('[data-filter-drawer]');
        if (drawer) {
          drawer.classList.add('is-open');
          drawer.setAttribute('aria-hidden', 'false');
          document.body.classList.add('filter-drawer-open');
          const panel = drawer.querySelector('.filter-drawer__panel');
          if (panel) panel.scrollTop = drawerScrollTop;
          if (activeFilterName) {
            const matching = Array.from(drawer.querySelectorAll('input')).find((input) => input.name === activeFilterName && input.value === activeFilterValue);
            matching?.focus({ preventScroll: true });
          }
        }
      }

      if (pushState) history.pushState({ econosmartCatalog: true }, '', cleanUrl.toString());

      document.dispatchEvent(new CustomEvent('econosmart:content-updated', {
        detail: { source: 'catalog' }
      }));

      if (scrollToResults) this.scrollToToolbar();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('EconoSmart catalog update failed:', error);
      window.location.href = cleanUrl.toString();
    } finally {
      this.setLoading(false);
    }
  }

  bindAfterRender() {
    this.root.addEventListener('click', (event) => this.handleClick(event));
    this.root.addEventListener('change', (event) => this.handleChange(event));
    this.root.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  setLoading(loading) {
    if (!this.root) return;
    this.root.classList.toggle('is-loading', loading);
    this.root.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  scrollToToolbar() {
    const toolbar = this.root.querySelector('[data-catalog-toolbar], [data-collection-toolbar]');
    if (!toolbar) return;

    const headerOffset = 90;
    const top = toolbar.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  setGridColumns(columns) {
    if (!Number.isFinite(columns) || columns < 2 || columns > 5) return;

    const grid = this.root.querySelector('[data-product-grid]');
    if (!grid) return;

    grid.style.setProperty('--product-columns', columns);
    this.root.querySelectorAll('[data-grid-columns]').forEach((button) => {
      const active = Number(button.dataset.gridColumns) === columns;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    try { localStorage.setItem(this.gridStorageKey, String(columns)); } catch (_) {}
  }

  applyGridPreference() {
    let columns = Number(this.root.dataset.gridColumnsDefault || 4);
    try {
      const stored = Number(localStorage.getItem(this.gridStorageKey));
      if (stored >= 2 && stored <= 5) columns = stored;
    } catch (_) {}

    this.setGridColumns(columns);
  }
}

let econoSmartCatalog = null;

function initEconoSmartCatalog() {
  const root = document.querySelector('[data-facets-root]');
  if (!root) return;
  econoSmartCatalog = new EconoSmartCatalog(root);
}

document.addEventListener('DOMContentLoaded', initEconoSmartCatalog);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-facets-root]')) initEconoSmartCatalog();
});

window.addEventListener('popstate', () => {
  if (econoSmartCatalog?.ajaxEnabled) {
    econoSmartCatalog.load(window.location.href, { pushState: false });
  }
});
